<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';

// Código que o servidor devolve com 422 quando a caixa ainda tem conversas e a
// requisição não confirmou a quantidade que será apagada junto. É o que divide
// a exclusão em duas etapas: a primeira descobre quanto histórico está em jogo,
// a segunda confirma aquele número.
const HISTORY_GUARD_ERROR = 'exclusao_apaga_historico';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const searchQuery = ref('');

// Segunda etapa. A caixa fica guardada aqui, e não em `selectedInbox`, porque
// o fechamento da primeira etapa limpa aquela referência.
const showHistoryWarning = ref(false);
const inboxPendingDeletion = ref({});
const conversationsCount = ref(0);
const isDeletingHistory = ref(false);

const inboxes = useMapGetter('inboxes/getInboxes');

const inboxesList = computed(() => {
  return inboxes.value?.slice().sort((a, b) => a.name.localeCompare(b.name));
});

const filteredInboxesList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return inboxesList.value;
  return picoSearch(inboxesList.value, query, ['name', 'channel_type']);
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const historyWarningMessage = computed(() =>
  t('INBOX_MGMT.DELETE.HISTORY.WARNING', {
    inboxName: inboxPendingDeletion.value.name,
    n: conversationsCount.value,
  })
);

const historyConfirmText = computed(() =>
  t('INBOX_MGMT.DELETE.HISTORY.CONFIRM', { n: conversationsCount.value })
);

// Devolve a quantidade de conversas quando a recusa é a trava de histórico, e
// null para qualquer outra falha, que segue tratada como erro comum.
const historyGuardCount = error => {
  const { status, data } = error?.response ?? {};
  if (status !== 422 || data?.error !== HISTORY_GUARD_ERROR) return null;

  const count = Number(data.conversations_count);
  return Number.isFinite(count) ? count : null;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const closeHistoryWarning = () => {
  showHistoryWarning.value = false;
  inboxPendingDeletion.value = {};
  conversationsCount.value = 0;
};

// A exclusão sai da API direto porque o store descarta o corpo da resposta de
// erro, e a quantidade de conversas só existe ali. A caixa sai da lista pelo
// store, sem reler do servidor: a lista vem do IndexedDB e a chave do cache só
// muda quando o DeleteObjectJob termina, então reler agora devolveria a caixa
// que acabou de ser excluída — e um segundo clique pediria outra exclusão dela.
const onInboxDeleted = inboxId => {
  store.commit('inboxes/DELETE_INBOXES', inboxId);
  useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
};

const confirmDeletion = async () => {
  const inbox = selectedInbox.value;
  closeDelete();

  try {
    await InboxesAPI.delete(inbox.id);
    onInboxDeleted(inbox.id);
  } catch (error) {
    const count = historyGuardCount(error);
    if (count === null) {
      useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
      return;
    }

    inboxPendingDeletion.value = inbox;
    conversationsCount.value = count;
    showHistoryWarning.value = true;
  }
};

const confirmHistoryDeletion = async () => {
  const inbox = inboxPendingDeletion.value;
  isDeletingHistory.value = true;

  try {
    await InboxesAPI.delete(inbox.id, {
      confirmDestroyHistory: conversationsCount.value,
    });
    closeHistoryWarning();
    onInboxDeleted(inbox.id);
  } catch (error) {
    const count = historyGuardCount(error);
    if (count === null) {
      closeHistoryWarning();
      useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
      return;
    }

    // Chegou conversa nova entre as duas chamadas. O número da tela envelheceu,
    // então ele é atualizado e a confirmação recomeça com o número novo.
    conversationsCount.value = count;
    useAlert(t('INBOX_MGMT.DELETE.HISTORY.COUNT_CHANGED'));
  } finally {
    isDeletingHistory.value = false;
  }
};

const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        :search-placeholder="$t('INBOX_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="inboxes"
      >
        <template v-if="inboxesList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.COUNT', { n: inboxesList.length }) }}
          </span>
        </template>
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button :label="$t('SETTINGS.INBOXES.NEW_INBOX')" size="sm" />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredInboxesList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="inbox in filteredInboxesList"
          :key="inbox.id"
          class="flex justify-between flex-row items-start gap-4 py-4"
        >
          <div class="flex items-center gap-4">
            <div
              v-if="inbox.avatar_url"
              class="bg-n-alpha-3 rounded-xl size-10 ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <Avatar
                :src="inbox.avatar_url"
                :name="inbox.name"
                :size="24"
                rounded-full
              />
            </div>
            <div
              v-else
              class="size-10 justify-center bg-n-alpha-3 rounded-xl ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <ChannelIcon class="size-6 text-n-slate-10" :inbox="inbox" />
            </div>
            <div class="flex flex-col items-start gap-1">
              <span class="block text-heading-3 text-n-slate-12 capitalize">
                {{ inbox.name }}
              </span>
              <ChannelName
                :channel-type="inbox.channel_type"
                :medium="inbox.medium"
                class="text-body-main text-n-slate-11"
              />
            </div>
          </div>
          <div class="flex gap-3 justify-end">
            <router-link
              :to="{
                name: 'settings_inbox_show',
                params: { inboxId: inbox.id },
              }"
            >
              <Button
                v-if="isAdmin"
                v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                icon="i-woot-settings"
                slate
                sm
              />
            </router-link>
            <Button
              v-if="isAdmin"
              v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
              data-testid="delete-inbox"
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="openDelete(inbox)"
            />
          </div>
        </div>
      </div>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />

    <woot-modal
      v-if="showHistoryWarning"
      v-model:show="showHistoryWarning"
      :on-close="closeHistoryWarning"
    >
      <woot-modal-header
        :header-title="$t('INBOX_MGMT.DELETE.HISTORY.TITLE')"
      />
      <div class="flex flex-col gap-4 px-8 pt-4 pb-8">
        <div
          data-testid="history-warning"
          class="flex flex-col gap-2 p-4 border rounded-xl bg-n-ruby-3 border-n-ruby-4 text-n-ruby-11"
        >
          <p class="mb-0 text-sm font-medium">
            {{ historyWarningMessage }}
          </p>
          <p class="mb-0 text-sm">
            {{ $t('INBOX_MGMT.DELETE.HISTORY.NO_RECOVERY') }}
          </p>
        </div>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.DELETE.HISTORY.KEEP_HINT') }}
        </p>
        <div class="flex items-center justify-end gap-2">
          <Button
            faded
            slate
            data-testid="history-cancel"
            :label="$t('INBOX_MGMT.DELETE.HISTORY.CANCEL')"
            @click="closeHistoryWarning"
          />
          <Button
            ruby
            data-testid="history-confirm"
            :label="historyConfirmText"
            :is-loading="isDeletingHistory"
            :disabled="isDeletingHistory"
            @click="confirmHistoryDeletion"
          />
        </div>
      </div>
    </woot-modal>
  </SettingsLayout>
</template>
